import * as React from 'react';
import styled from 'styled-components';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';

import {
  getMyInspectorMotd,
  InspectorMotdMessage,
  sendInspectorMotd,
  updateInspectorMotd,
} from '../../api/inspectorMotd';
import useCanSendInspectorMotd from '../../hooks/useCanSendInspectorMotd';
import { ModalDefaultDraggable } from '../../molecules';
import ModalInfo from '../../molecules/ModalInfo';
import InspectorMotdForm from '../../forms/InspectorMotdForm';

type Props = {
  /** Bumped after a successful send or edit so the message list refetches. */
  onSent?: () => void;
};

/**
 * Send bar for the Inspector Message of the Day, on the inspector dashboard.
 *
 * Renders nothing unless the current user is the single inspector a Super
 * Admin designated in Settings > Message Setup. The backend enforces the same
 * rule, so this is a UI convenience, not the access control.
 *
 * The compose form is the same modal the Messages page uses, so the two entry
 * points behave identically.
 */
const InspectorMotdComposer = ({ onSent }: Props = {}) => {
  const { canSend } = useCanSendInspectorMotd();
  const [today, setToday] = React.useState<
    InspectorMotdMessage[]
  >([]);
  const [editing, setEditing] = React.useState<
    InspectorMotdMessage
  >(null);
  const [isModalOpen, setIsModalOpen] = React.useState(
    false,
  );
  const [errors, setErrors] = React.useState(null);
  const [feedback, setFeedback] = React.useState<
    string | null
  >(null);
  const [isError, setIsError] = React.useState(false);

  const loadToday = React.useCallback(() => {
    getMyInspectorMotd()
      .then(response => setToday(response.data || []))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (canSend) loadToday();
  }, [canSend, loadToday]);

  if (!canSend) {
    return null;
  }

  const [latest] = today;

  const openCompose = () => {
    setEditing(null);
    setErrors(null);
    setIsModalOpen(true);
  };

  const openEdit = () => {
    setEditing(latest);
    setErrors(null);
    setIsModalOpen(true);
  };

  const handleSubmit = (fields: {
    id?: number;
    text: string;
  }) => {
    const request = fields.id
      ? updateInspectorMotd(fields.id, {
          text: fields.text,
        })
      : sendInspectorMotd({ text: fields.text });

    return request
      .then(() => {
        setIsModalOpen(false);
        setEditing(null);
        setIsError(false);
        setFeedback(
          fields.id
            ? 'Message updated.'
            : 'Message sent to all inspectors.',
        );
        loadToday();
        if (onSent) onSent();
      })
      .catch(error => {
        const data = error?.response?.data;
        if (data && typeof data === 'object') {
          // Feeds the form's field-level errors and clears its spinner.
          setErrors({ ...data });
          return;
        }
        setIsError(true);
        setFeedback('Could not save the message.');
      });
  };

  return (
    <>
      <Bar>
        <BarLabel>
          {latest
            ? "Today's message is out"
            : 'No message sent today'}
        </BarLabel>
        <Tools>
          {latest && (
            <IconButton
              type="button"
              title="Edit today's message"
              aria-label="Edit today's message"
              onClick={openEdit}
            >
              <EditIcon />
            </IconButton>
          )}
          <IconButton
            type="button"
            title="Send a message of the day"
            aria-label="Send a message of the day"
            onClick={openCompose}
          >
            <AddIcon />
          </IconButton>
        </Tools>
      </Bar>

      {isModalOpen && (
        <ModalDefaultDraggable
          onClose={() => setIsModalOpen(false)}
          title={
            editing
              ? 'Update Message of the Day'
              : 'Send Message of the Day'
          }
        >
          <InspectorMotdForm
            initialValues={editing}
            initialErrors={errors}
            modalError={false}
            onSubmit={handleSubmit}
          />
        </ModalDefaultDraggable>
      )}

      {feedback && (
        <ModalInfo
          timeToClose={5000}
          isError={isError}
          errorMsg={isError ? feedback : ''}
          onClose={() => setFeedback(null)}
        />
      )}
    </>
  );
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
  margin-bottom: 16px;
`;

const BarLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tools = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease,
    border-color 0.15s ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: var(--primary-600);
    background: var(--primary-50);
    border-color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

export default InspectorMotdComposer;
